import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Menu,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import { toast } from '../../Components/Toast';
import { confirmDialog } from '../../Components/ConfirmDialog';
import {
  LEAD_NOT_FOUND_MESSAGE,
  LEAD_PROFILE_NOT_ENABLED_MESSAGE,
  addLeadNote,
  buildLeadProfilePatch,
  deleteLead,
  getLead,
  getLeadErrorMessage,
  isLeadProfileAvailable,
  isLeadProfileNotEnabled,
  markLeadInquiriesSeen,
  readLead,
  readLeadDuplicateId,
  readApiErrorCode,
  readLeadInterestUpdate,
  readLeadProfileErrorField,
  syncLeadNameFromProfile,
  updateLead,
  updateLeadInterest,
} from '../../services/leadsService';
import { leadHasUnseenInquiries } from '../leadCollections';
import { invalidateCollectionLeadCounts } from '../../hooks/useCollectionLeadCounts';
import {
  describeInterestStockUpdate,
  describeManualInquiryCreated,
  getLeadAuthorship,
  getLeadTemperatureLabel,
  getLeadOriginLabel,
  groupLeadInterests,
  leadActivityFilterOptions,
  isPlausibleEmail,
  isPlausiblePhone,
} from '../leadsConfig';
import { formatDateTimeRelativeAR, formatDateTimeShortAR } from '../../Utils/dateTimeFormat';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';
import { sanitizePhoneInput } from '../../Utils/brandForm';
import PanelHeader, { panelCloseIconButtonSx, panelTitleSx } from '../../Map/components/PanelShell/PanelHeader';
import LeadChannelPicker from './LeadChannelPicker';
import LeadProfileFields from './LeadProfileFields';
import LeadProfileSummary from './LeadProfileSummary';
import { leadProfileFromForm, leadProfileToForm, validateLeadProfileForm } from '../leadProfile';
import InfoNotice from '../../Components/InfoNotice';
import ActionMenuItem, { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../../Map/components/ActionMenuItem';
import {
  emptyStateBoxSx,
  emptyStateTitleSx,
  scrollableListZoneSx,
} from '../../Map/components/UserLotListsPanel/UserLotListsPanel.styles';
import useLeadItemOpener from './useLeadItemOpener';
import LeadItemChips from './LeadItemChips';
import LeadRecordFilter from './LeadRecordFilter';
import LeadActivity from './LeadActivity';
import LeadActorLine from './LeadActorLine';
import LeadManualInquiryDialog from './LeadManualInquiryDialog';
import LeadAssignRecordDialog from './LeadAssignRecordDialog';
import { isManualInquiryDisabled } from '../../services/manualInquiryService';
import {
  LEADS_BG_ELEVATED,
  LEADS_FORM_FIELD_GAP,
  LEADS_FORM_SECTION_GAP,
  LEADS_PANEL_BG,
  LEADS_TEXT_MUTED,
  LEADS_TEXT_PRIMARY,
  LEADS_TEXT_SECONDARY,
  getLeadTemperaturePillSx,
  leadsAlertSx,
  leadsGhostButtonSx,
  leadsPrimaryButtonSx,
  leadsSectionTitleSx,
  leadsTextFieldSx,
  leadsToolbarSkeletonSx,
} from './leadsUi.styles';

function formatAltaEn(value) {
  if (!value) return '';
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  const date = dt.toLocaleDateString('es-AR', { day: 'numeric', month: 'numeric', year: 'numeric' });
  return `Alta en ${date}`;
}

const sectionSx = {
  bgcolor: LEADS_BG_ELEVATED,
  borderRadius: '10px',
  px: 1.75,
  py: 1.5,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

const contactRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  color: LEADS_TEXT_PRIMARY,
  fontSize: '13px',
  minWidth: 0,
  '& .MuiSvgIcon-root': { fontSize: 16, color: LEADS_TEXT_SECONDARY },
};

const linkSx = {
  color: LEADS_TEXT_PRIMARY,
  textDecorationColor: 'rgba(255,255,255,0.25)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&:hover': { color: '#FFFFFF' },
};

function LeadDetailSkeleton() {
  const bar = leadsToolbarSkeletonSx;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} aria-busy="true">
      <Box aria-hidden>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, minHeight: 26 }}>
          <Typography sx={leadsSectionTitleSx}>Datos</Typography>
          <Skeleton variant="circular" width={18} height={18} sx={{ bgcolor: '#383838' }} />
        </Box>
        <Box sx={sectionSx}>
          <Skeleton variant="rounded" width="46%" height={14} sx={bar} />
          <Skeleton variant="rounded" width="62%" height={14} sx={bar} />
          <Skeleton variant="rounded" width="78%" height={14} sx={bar} />
        </Box>
      </Box>

      <Box aria-hidden>
        <Typography sx={{ ...leadsSectionTitleSx, mb: 0.75 }}>Intereses y consultas</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Skeleton variant="rounded" height={64} sx={{ ...bar, borderRadius: '8px' }} />
          <Skeleton variant="rounded" height={64} sx={{ ...bar, borderRadius: '8px' }} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} aria-hidden>
        <Typography sx={leadsSectionTitleSx}>Agregar nota</Typography>
        <Skeleton variant="rounded" height={72} sx={{ ...bar, borderRadius: '8px' }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Skeleton variant="rounded" width={148} height={40} sx={{ ...bar, borderRadius: '8px' }} />
        </Box>
      </Box>

      <Box aria-hidden>
        <Typography sx={{ ...leadsSectionTitleSx, mb: 1 }}>Actividad</Typography>
        <Skeleton variant="rounded" width={36} height={11} sx={{ ...bar, mb: 0.75 }} />
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              pl: 2.25,
              pb: i === 2 ? 0 : 1.5,
              ml: '4px',
              borderLeft: i === 2 ? '1px solid transparent' : '1px solid rgba(255,255,255,0.10)',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Skeleton variant="rounded" width={`${56 - i * 8}%`} height={12} sx={bar} />
              <Skeleton variant="rounded" width={36} height={11} sx={bar} />
            </Box>
            {i === 0 && (
              <Skeleton variant="rounded" height={36} sx={{ ...bar, borderRadius: '8px' }} />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function whatsappHref(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length >= 8 ? `https://wa.me/${digits}` : '';
}

function editFormFromLead(lead) {
  return {
    name: lead?.name || '',
    phone: lead?.phone || '',
    email: lead?.email || '',
    interest: lead?.interest || '',
    channel: lead?.channel || '',
  };
}

/** Campos del lead que cambiaron en el form (trim; vacío → `null`). */
function leadFieldChanges(form, lead) {
  const next = {
    name: form.name.trim(),
    phone: form.phone.trim(),
    email: form.email.trim(),
    interest: form.interest.trim(),
    channel: form.channel || '',
  };
  const changes = {};
  Object.keys(next).forEach((key) => {
    if (next[key] !== (lead?.[key] || '')) changes[key] = next[key] || null;
  });
  return { next, changes };
}

const DISCARD_CONFIRM = {
  title: '¿Descartar los cambios?',
  description: 'Hay cambios sin guardar en los datos del lead.',
  confirmLabel: 'Descartar',
  cancelLabel: 'Seguir editando',
  variant: 'warning',
};

/**
 * Detalle de un lead: etapa, datos, intereses, notas y timeline.
 *
 * @param {{ leadId: string, ownerUserId?: string|null, canDelete: boolean,
 *   onBack: () => void, onClose: () => void, onChanged?: (lead: object) => void,
 *   onDeleted?: (leadId: string) => void, onGone?: (leadId: string) => void,
 *   onOpenLead?: (leadId: string) => void,
 *   onOpenItemTarget?: (target: object) => void }} props
 *   `onOpenItemTarget`: navegación de MapPage al tocar un interés (ver
 *   `useLeadItemOpener`). Sin él los chips no son clicables.
 */
export default function LeadDetail({
  leadId,
  ownerUserId = null,
  canDelete,
  onBack,
  onClose,
  onChanged,
  onDeleted,
  onGone,
  onOpenLead,
  onOpenItemTarget,
  cardUnseenInquiries = 0,
  /** Texto del volver (por defecto al índice; desde una UF, “Volver a UF 3”). */
  backLabel = 'Volver a leads',
}) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(editFormFromLead(null));
  const [editError, setEditError] = useState('');
  const [editDuplicateId, setEditDuplicateId] = useState('');
  const [saving, setSaving] = useState(false);
  // Datos del cliente (`profile`): form, errores por campo y aviso del 503.
  const [profileForm, setProfileForm] = useState(() => leadProfileToForm(null));
  const [profileErrors, setProfileErrors] = useState({});
  const [profileNotice, setProfileNotice] = useState('');
  // 503 en esta ficha: el perfil queda fuera del form (el servicio además lo
  // apaga para la sesión: `isLeadProfileDisabled`).
  const [profileOff, setProfileOff] = useState(false);
  const [note, setNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [stageSavingId, setStageSavingId] = useState('');
  // V3-3: 403 al cambiar una etapa → la ficha queda en solo lectura.
  const [stageForbidden, setStageForbidden] = useState(false);
  const writeAbortRef = useRef(null);
  const [reloadSeq, setReloadSeq] = useState(0);
  // Consulta manual (desde la ficha: lead fijo) y «Asignar registro».
  const [manualOpen, setManualOpen] = useState(false);
  const [assignInquiry, setAssignInquiry] = useState(null);
  const { scrollRef, fadeTop, fadeBottom, refreshScrollEdges } = useScrollEdgeFade();

  const callbacksRef = useRef({ onChanged, onGone });
  callbacksRef.current = { onChanged, onGone };
  const cardUnseenRef = useRef(cardUnseenInquiries);
  cardUnseenRef.current = cardUnseenInquiries;
  // F2: una vez por ficha abierta (no en cada recarga del GET).
  const markedSeenRef = useRef('');

  useEffect(() => () => writeAbortRef.current?.abort(), []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError('');
    (async () => {
      try {
        const res = await getLead(leadId, { ownerUserId, signal: controller.signal });
        if (controller.signal.aborted) return;
        const data = res?._httpOk ? readLead(res) : null;
        if (data) {
          setLead(data);
          if (markedSeenRef.current !== leadId && leadHasUnseenInquiries(data, cardUnseenRef.current)) {
            markedSeenRef.current = leadId;
            // Abrir la ficha = vi sus consultas. Los puntos de “nueva” quedan
            // en esta vista; tarjeta y badge de la lista se actualizan. Falla
            // (API vieja, 403, red) → en silencio.
            markLeadInquiriesSeen(leadId, { ownerUserId }).then((seenRes) => {
              if (!seenRes?._httpOk) return;
              const remaining = Number(seenRes?.data?.unseen_inquiry_count);
              callbacksRef.current.onChanged?.({
                ...data,
                unseen_inquiry_count: Number.isFinite(remaining) && remaining > 0 ? remaining : 0,
              });
              invalidateCollectionLeadCounts();
            }).catch(() => {});
          }
          return;
        }
        if (res?._httpStatus === 404) {
          toast.error(LEAD_NOT_FOUND_MESSAGE);
          callbacksRef.current.onGone?.(leadId);
          return;
        }
        setLoadError(getLeadErrorMessage(res, 'No se pudo abrir el lead.'));
      } catch (err) {
        if (err?.name !== 'AbortError') setLoadError('No se pudo abrir el lead.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [leadId, ownerUserId, reloadSeq]);

  // Las escrituras (updatelead, nota) no traen `inquiries`: se conservan las del GET.
  // `created_by` no cambia: si una respuesta vieja no lo trae, queda el del GET.
  const applyLead = useCallback((next) => {
    if (!next?.id) return;
    setLead((prev) => ({
      ...next,
      ...(next.inquiries === undefined && prev?.inquiries ? { inquiries: prev.inquiries } : {}),
      ...(next.created_by === undefined && prev?.created_by ? { created_by: prev.created_by } : {}),
    }));
    callbacksRef.current.onChanged?.(next);
  }, []);

  const handleGone = useCallback((id) => callbacksRef.current.onGone?.(id), []);

  const fromLead = useMemo(
    () => (lead?.id ? { leadId: String(lead.id), leadName: lead.name || '' } : null),
    [lead?.id, lead?.name],
  );
  const { openItem, openingItemId, canOpen: canOpenItems } = useLeadItemOpener({
    ownerUserId,
    onOpenTarget: onOpenItemTarget,
    fromLead,
  });

  const startWrite = () => {
    writeAbortRef.current?.abort();
    const controller = new AbortController();
    writeAbortRef.current = controller;
    return controller;
  };

  const handleStartEdit = () => {
    setMenuAnchor(null);
    if (editing) return;
    setEditForm(editFormFromLead(lead));
    setProfileForm(leadProfileToForm(lead?.profile));
    setProfileErrors({});
    setProfileNotice('');
    setEditError('');
    setEditDuplicateId('');
    setEditing(true);
  };

  const profileEditable = isLeadProfileAvailable(lead) && !profileOff;
  // Diff del perfil (solo lo que cambió) + sync de `name` (lo maneja el Front).
  const buildEditBody = () => {
    const { next, changes } = leadFieldChanges(editForm, lead);
    const body = { id: lead.id, ...changes };
    if (profileEditable) {
      const nextProfile = leadProfileFromForm(profileForm);
      const profilePatch = buildLeadProfilePatch(lead.profile, nextProfile);
      if (Object.keys(profilePatch).length) {
        body.profile = profilePatch;
        // Si el corredor no tocó `name` a mano: se alinea con «nombre apellido»
        // cuando estaba vacío o era la combinación anterior (ver docs/maps/leads.md).
        if (!('name' in changes)) {
          const synced = syncLeadNameFromProfile({ name: lead.name, prev: lead.profile, next: nextProfile });
          if (synced) body.name = synced;
        }
      }
    }
    return { next, body };
  };
  const editDirty = Boolean(editing && lead) && Object.keys(buildEditBody().body).length > 1;
  const editDirtyRef = useRef(false);
  editDirtyRef.current = editDirty;

  // Aviso de cambios sin guardar: Cancelar, volver y cerrar el panel. Sin
  // cambios, la acción es inmediata (sincrónica).
  const guardDiscard = (action) => {
    if (!editDirtyRef.current) { action(); return; }
    Promise.resolve(confirmDialog(DISCARD_CONFIRM)).then((answer) => {
      if (answer?.isConfirmed) action();
    });
  };
  const handleCancelEdit = () => guardDiscard(() => setEditing(false));
  const handleBack = () => guardDiscard(() => onBack?.());
  const handleClose = () => guardDiscard(() => onClose?.());

  const handleSaveEdit = async (e) => {
    e?.preventDefault?.();
    if (!lead || saving) return;
    const { next, body } = buildEditBody();
    if (!next.name) { setEditError('Cargá el nombre.'); return; }
    if (!next.email && !next.phone) { setEditError('Dejá al menos un teléfono o un email.'); return; }
    if (!isPlausibleEmail(next.email)) { setEditError('Ingresá un email válido.'); return; }
    if (!isPlausiblePhone(next.phone)) {
      setEditError('Ingresá un teléfono válido (8–15 dígitos; +, espacios o guiones).');
      return;
    }
    if (profileEditable) {
      const localErrors = validateLeadProfileForm(profileForm);
      setProfileErrors(localErrors);
      if (Object.keys(localErrors).length) { setEditError('Revisá los datos del cliente.'); return; }
    }
    if (Object.keys(body).length === 1) { setEditing(false); return; }
    const controller = startWrite();
    setSaving(true);
    setEditError('');
    setEditDuplicateId('');
    setProfileNotice('');
    try {
      const res = await updateLead(body, { ownerUserId, signal: controller.signal });
      if (controller.signal.aborted) return;
      const updated = res?._httpOk ? readLead(res) : null;
      if (updated) {
        applyLead(updated);
        setEditing(false);
        toast.success('Datos guardados');
        return;
      }
      if (res?._httpStatus === 404) {
        toast.error(LEAD_NOT_FOUND_MESSAGE);
        handleGone(lead.id);
        return;
      }
      // 503: el perfil no está habilitado (falta la SQL). No se guardó nada:
      // se ocultan sus campos y el resto se puede guardar de nuevo.
      if (isLeadProfileNotEnabled(res)) {
        setProfileOff(true);
        setProfileNotice(`${LEAD_PROFILE_NOT_ENABLED_MESSAGE} Guardá de nuevo para guardar el resto.`);
        return;
      }
      const profileField = readLeadProfileErrorField(res);
      if (profileField && profileField !== 'profile') {
        const apiText = typeof res?.error === 'string' && res.error.trim() ? res.error.trim() : 'Revisá este dato.';
        setProfileErrors({ [profileField]: apiText });
        setEditError('Revisá los datos del cliente.');
        return;
      }
      const dupId = readLeadDuplicateId(res);
      setEditDuplicateId(dupId && dupId !== lead.id ? dupId : '');
      setEditError(dupId ? 'Ya hay otro lead con ese email o teléfono.' : getLeadErrorMessage(res, 'No se pudo guardar.'));
    } catch (err) {
      if (err?.name !== 'AbortError') setEditError('No se pudo guardar.');
    } finally {
      if (writeAbortRef.current === controller) setSaving(false);
    }
  };

  const handleAddNote = async () => {
    const text = note.trim();
    if (!lead || !text || noteSaving) return;
    const controller = startWrite();
    setNoteSaving(true);
    try {
      const res = await addLeadNote({ leadId: lead.id, note: text }, { ownerUserId, signal: controller.signal });
      if (controller.signal.aborted) return;
      if (res?._httpOk) {
        const row = res?.data && typeof res.data === 'object' ? res.data : null;
        setNote('');
        if (row?.id) {
          // La nota también actualiza `updated_by` / `updated_at` del lead (API).
          const next = {
            ...lead,
            activity: [row, ...(lead.activity || [])],
            ...(row.actor ? { updated_by: row.actor } : {}),
            ...(row.created_at ? { updated_at: row.created_at } : {}),
          };
          applyLead(next);
        } else {
          setReloadSeq((n) => n + 1);
        }
        return;
      }
      if (res?._httpStatus === 404) {
        toast.error(LEAD_NOT_FOUND_MESSAGE);
        handleGone(lead.id);
        return;
      }
      toast.error(getLeadErrorMessage(res, 'No se pudo guardar la nota.'));
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error('No se pudo guardar la nota.');
    } finally {
      if (writeAbortRef.current === controller) setNoteSaving(false);
    }
  };

  /**
   * V3-3: PATCH /updateleadinterest. Actualiza el ítem y el historial en el
   * lugar y avisa al panel (`onChanged` → al volver recarga contadores e
   * `interest_summary`). Devuelve `true` si se guardó (cierra el diálogo).
   */
  const handleChangeInterestStage = async (item, change) => {
    if (!lead || !item?.id || stageSavingId) return false;
    const controller = startWrite();
    setStageSavingId(item.id);
    try {
      const res = await updateLeadInterest(
        { leadId: lead.id, itemId: item.id, ...change },
        { ownerUserId, signal: controller.signal },
      );
      if (controller.signal.aborted) return false;
      const parsed = res?._httpOk ? readLeadInterestUpdate(res) : null;
      if (parsed) {
        const next = {
          ...lead,
          items: (lead.items || []).map((it) => (it.id === parsed.item.id ? { ...it, ...parsed.item } : it)),
          ...(parsed.activity ? { activity: [parsed.activity, ...(lead.activity || [])] } : {}),
          ...(parsed.activity?.actor ? { updated_by: parsed.activity.actor } : {}),
          ...(parsed.activity?.created_at ? { updated_at: parsed.activity.created_at } : {}),
        };
        applyLead(next);
        toast.success('Etapa actualizada');
        const stockMsg = describeInterestStockUpdate(parsed.stockUpdate);
        if (stockMsg) (stockMsg.kind === 'success' ? toast.success : toast.info)(stockMsg.text);
        return true;
      }
      if (res?._httpOk) {
        // 200 sin ítem (respuesta inesperada): recargar.
        setReloadSeq((n) => n + 1);
        return true;
      }
      const code = readApiErrorCode(res);
      if (res?._httpStatus === 403) {
        setStageForbidden(true);
        toast.error('No tenés permiso para cambiar etapas en este espacio.');
        return true;
      }
      if (res?._httpStatus === 404 && code === 'ITEM_NOT_FOUND') {
        toast.error(getLeadErrorMessage(res));
        setReloadSeq((n) => n + 1);
        return true;
      }
      if (res?._httpStatus === 404) {
        toast.error(LEAD_NOT_FOUND_MESSAGE);
        handleGone(lead.id);
        return true;
      }
      toast.error(getLeadErrorMessage(res, 'No se pudo cambiar la etapa.'));
      return code === 'ITEM_NOT_TRACKABLE';
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error('No se pudo cambiar la etapa.');
      return false;
    } finally {
      if (writeAbortRef.current === controller) setStageSavingId('');
    }
  };

  const handleDelete = async () => {
    setMenuAnchor(null);
    if (!lead || !canDelete || deleting) return;
    const answer = await confirmDialog({
      title: '¿Archivar este lead?',
      description: `${lead.name || 'El lead'} sale de la lista; sus consultas, notas e historial quedan. Si vuelve a consultar, se desarchiva solo.`,
      confirmLabel: 'Archivar',
      cancelLabel: 'Cancelar',
      variant: 'warning',
    });
    if (!answer?.isConfirmed) return;
    const controller = startWrite();
    setDeleting(true);
    try {
      const res = await deleteLead(lead.id, { signal: controller.signal });
      if (controller.signal.aborted) return;
      if (res?._httpOk || res?._httpStatus === 404) {
        toast.success('Lead archivado');
        onDeleted?.(lead.id);
        return;
      }
      toast.error(getLeadErrorMessage(res, 'No se pudo archivar el lead.'));
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error('No se pudo archivar el lead.');
    } finally {
      if (writeAbortRef.current === controller) setDeleting(false);
    }
  };

  const detailLoading = loading && !lead;
  const items = useMemo(
    () => (Array.isArray(lead?.items) ? lead.items : []),
    [lead?.items],
  );
  const inquiries = useMemo(
    () => (Array.isArray(lead?.inquiries) ? lead.inquiries : null),
    [lead?.inquiries],
  );
  const authorship = useMemo(() => getLeadAuthorship(lead), [lead]);
  const activityEntries = useMemo(
    () => (Array.isArray(lead?.activity) ? lead.activity : []),
    [lead?.activity],
  );
  const interestGroups = useMemo(
    () => groupLeadInterests(items, inquiries),
    [items, inquiries],
  );
  const recordOptions = useMemo(
    () => leadActivityFilterOptions(interestGroups.groups, activityEntries, interestGroups.others),
    [interestGroups, activityEntries],
  );
  const [recordFilter, setRecordFilter] = useState('all');
  const activeRecord = recordFilter !== 'all' && recordOptions.some((option) => option.id === recordFilter)
    ? recordFilter
    : 'all';
  // Solo lectura: 403 previo o la API dice que no puede gestionar (`viewer_can_manage`).
  const canEditStages = Boolean(lead) && !stageForbidden && lead.viewer_can_manage !== false;
  // Nueva consulta / asignar registro: dueño o editor del espacio (mismo criterio).
  const canManageInquiries = canEditStages;
  const presetLead = useMemo(
    () => (lead ? { id: lead.id, name: lead.name, phone: lead.phone, email: lead.email } : null),
    [lead],
  );
  // La consulta nueva sale del GET; el panel recarga contadores al volver.
  const refreshAfterInquiry = () => {
    setReloadSeq((n) => n + 1);
    if (lead) callbacksRef.current.onChanged?.(lead);
  };
  const handleManualCreated = (result) => {
    setManualOpen(false);
    toast.success(describeManualInquiryCreated(result));
    refreshAfterInquiry();
  };
  const handleAssignDone = ({ ok, refresh, message }) => {
    setAssignInquiry(null);
    if (ok) toast.success('Registro asignado');
    else if (message) toast.error(message);
    if (ok || refresh) refreshAfterInquiry();
  };
  useEffect(() => {
    refreshScrollEdges();
  }, [refreshScrollEdges, lead, editing, detailLoading]);
  const waHref = whatsappHref(lead?.phone);
  const subtitle = lead
    ? [
      getLeadOriginLabel(lead).label,
      formatAltaEn(lead.created_at),
    ].filter(Boolean).join(' · ')
    : '';
  const temperatureLabel = lead ? getLeadTemperatureLabel(lead.temperature) : '';
  const lastTouchWhen = lead
    ? formatDateTimeRelativeAR(lead.last_touch_at || lead.last_activity_at || lead.updated_at)
    : '';
  const temperatureTooltip = lastTouchWhen
    ? `Último contacto ${lastTouchWhen}`
    : (temperatureLabel ? `Nivel de interés: ${temperatureLabel}` : '');

  return (
    <>
      <Box sx={{ bgcolor: LEADS_PANEL_BG }}>
        <PanelHeader
          title={detailLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Skeleton variant="rounded" width={168} height={20} sx={leadsToolbarSkeletonSx} aria-hidden />
              <Skeleton variant="rounded" width={72} height={22} sx={{ ...leadsToolbarSkeletonSx, borderRadius: '11px' }} aria-hidden />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Typography component="h2" variant="h6" sx={{ ...panelTitleSx, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {lead?.name || 'Lead'}
              </Typography>
              {temperatureLabel ? (
                <Tooltip title={temperatureTooltip} placement="bottom" arrow>
                  <Box
                    component="span"
                    role="status"
                    aria-label="Nivel de interés del lead"
                    data-testid="lead-detail-temperature"
                    sx={getLeadTemperaturePillSx(lead.temperature)}
                  >
                    {temperatureLabel}
                  </Box>
                </Tooltip>
              ) : null}
            </Box>
          )}
          subtitle={detailLoading ? (
            <Skeleton variant="rounded" width={132} height={14} sx={leadsToolbarSkeletonSx} aria-hidden />
          ) : subtitle}
          startAdornment={(
            <Tooltip title={backLabel}>
              <IconButton size="small" onClick={handleBack} aria-label={backLabel} sx={{ ...panelCloseIconButtonSx, mr: 0.5 }}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
          )}
          actions={(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {lead && (
                <IconButton
                  size="small"
                  aria-label="Acciones del lead"
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  sx={panelCloseIconButtonSx}
                >
                  <MoreVertIcon />
                </IconButton>
              )}
              <IconButton size="small" onClick={handleClose} aria-label="Cerrar panel" sx={panelCloseIconButtonSx}>
                <CloseIcon />
              </IconButton>
            </Box>
          )}
        />
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={ACTION_MENU_PAPER_PROPS}
        MenuListProps={ACTION_MENU_LIST_PROPS}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <ActionMenuItem icon={<EditOutlinedIcon fontSize="small" />} label="Editar datos" onClick={handleStartEdit} />
        {canDelete && (
          <ActionMenuItem
            icon={<ArchiveOutlinedIcon fontSize="small" />}
            label="Archivar lead"
            loading={deleting}
            onClick={handleDelete}
          />
        )}
      </Menu>

      <Box
        ref={scrollRef}
        sx={{
          ...scrollableListZoneSx,
          flex: 1,
          bgcolor: LEADS_PANEL_BG,
          px: 2.5,
          py: 2,
          ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
        }}
      >
        {detailLoading ? (
          <LeadDetailSkeleton />
        ) : loadError && !lead ? (
          <Box sx={{ ...emptyStateBoxSx, minHeight: 220 }}>
            <Typography sx={emptyStateTitleSx}>{loadError}</Typography>
            <Button onClick={() => setReloadSeq((n) => n + 1)} sx={{ ...leadsGhostButtonSx, mt: 1 }}>Reintentar</Button>
          </Box>
        ) : lead ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Datos */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, minHeight: 26 }}>
                <Typography sx={leadsSectionTitleSx}>Datos</Typography>
                {!editing && (
                  <Tooltip title="Editar" placement="top" arrow>
                    <IconButton
                      size="small"
                      onClick={handleStartEdit}
                      aria-label="Editar"
                      sx={{
                        color: LEADS_TEXT_SECONDARY,
                        p: '4px',
                        '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
                      }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            {editing ? (
              <Box component="form" onSubmit={handleSaveEdit} noValidate sx={{ ...sectionSx, gap: LEADS_FORM_SECTION_GAP }}>
                {/* Mismos tokens que «Nuevo lead» / «Nueva consulta». */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: LEADS_FORM_FIELD_GAP }}>
                  <TextField
                    label="Nombre"
                    size="small"
                    value={editForm.name}
                    onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                    inputProps={{ maxLength: 120 }}
                    sx={leadsTextFieldSx}
                  />
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: LEADS_FORM_FIELD_GAP, alignItems: 'start' }}>
                    <TextField
                      label="Teléfono"
                      size="small"
                      value={editForm.phone}
                      onChange={(e) => setEditForm((p) => ({ ...p, phone: sanitizePhoneInput(e.target.value) }))}
                      autoComplete="tel"
                      inputProps={{ maxLength: 40, inputMode: 'tel' }}
                      sx={leadsTextFieldSx}
                    />
                    <TextField
                      label="Email"
                      size="small"
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))}
                      autoComplete="email"
                      inputProps={{ maxLength: 254, inputMode: 'email' }}
                      sx={leadsTextFieldSx}
                    />
                  </Box>
                </Box>
                {/* Datos del cliente (`profile`): debajo de teléfono y mail. */}
                {profileNotice ? (
                  <InfoNotice role="status" data-testid="lead-profile-not-enabled" title={profileNotice} />
                ) : profileEditable ? (
                  <LeadProfileFields
                    form={profileForm}
                    errors={profileErrors}
                    disabled={saving}
                    leadName={editForm.name}
                    onChange={(patchForm) => {
                      setProfileForm((prev) => ({ ...prev, ...patchForm }));
                      setProfileErrors((prev) => {
                        const keys = Object.keys(patchForm);
                        if (!keys.some((k) => prev[k])) return prev;
                        const nextErrors = { ...prev };
                        keys.forEach((k) => { delete nextErrors[k]; });
                        return nextErrors;
                      });
                    }}
                  />
                ) : null}
                <TextField
                  label="¿Qué busca?"
                  size="small"
                  multiline
                  minRows={2}
                  maxRows={6}
                  value={editForm.interest}
                  onChange={(e) => setEditForm((p) => ({ ...p, interest: e.target.value }))}
                  inputProps={{ maxLength: 2000 }}
                  sx={leadsTextFieldSx}
                />
                <LeadChannelPicker
                  value={editForm.channel}
                  disabled={saving}
                  onChange={(channel) => setEditForm((p) => ({ ...p, channel }))}
                />
                {editError && editDuplicateId ? (
                  <InfoNotice
                    role="alert"
                    data-testid="lead-duplicate-notice"
                    title={editError}
                    actionLabel={onOpenLead ? 'Abrir existente' : ''}
                    onAction={onOpenLead ? () => onOpenLead(editDuplicateId) : undefined}
                  />
                ) : editError ? (
                  <Alert severity="error" variant="outlined" sx={leadsAlertSx}>
                    {editError}
                  </Alert>
                ) : null}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                  {editDirty && !saving ? (
                    <Typography role="status" data-testid="lead-edit-dirty" sx={{ fontSize: '12px', color: LEADS_TEXT_MUTED, mr: 'auto' }}>
                      Cambios sin guardar
                    </Typography>
                  ) : null}
                  <Button onClick={handleCancelEdit} disabled={saving} sx={leadsGhostButtonSx}>Cancelar</Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                    sx={leadsPrimaryButtonSx}
                    startIcon={saving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
                  >
                    Guardar
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={sectionSx}>
                {lead.phone && (
                  <Box sx={contactRowSx}>
                    <PhoneOutlinedIcon />
                    <Link href={`tel:${lead.phone}`} sx={linkSx}>{lead.phone}</Link>
                    {waHref && (
                      <Tooltip title="Abrir WhatsApp">
                        <IconButton
                          size="small"
                          component="a"
                          href={waHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Abrir WhatsApp"
                          sx={{ p: '2px', '& .MuiSvgIcon-root': { color: '#5FB878' } }}
                        >
                          <WhatsAppIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
                {lead.email && (
                  <Box sx={contactRowSx}>
                    <MailOutlineIcon />
                    <Link href={`mailto:${lead.email}`} sx={linkSx}>{lead.email}</Link>
                  </Box>
                )}
                {/* Datos del cliente: solo los campos con dato (no va en listado ni tablero). */}
                <LeadProfileSummary profile={lead.profile} />
                <Typography sx={{ fontSize: '13px', color: lead.interest ? LEADS_TEXT_PRIMARY : LEADS_TEXT_MUTED, whiteSpace: 'pre-wrap' }}>
                  {lead.interest || 'Sin detalle de qué busca.'}
                </Typography>
                {authorship.length > 0 && (
                  <Box data-testid="lead-authorship" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.25 }}>
                    {authorship.map((row) => (
                      <LeadActorLine
                        key={row.key}
                        actor={row.actor}
                        prefix={row.label}
                        suffix={formatDateTimeShortAR(row.at) || ''}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}
            </Box>

            {/* En edición solo queda «Datos»: consultas, notas y actividad
                vuelven al cancelar o guardar, para no mezclar el form con el scroll. */}
            {!editing && (
            <>
            {/* TODO(leads): acá van a ir las preferencias derivadas de las consultas
                cuando exista el contrato en la API (los datos fijos del cliente ya
                están en «Datos»). `interest_filters` v1 se dio de baja el 20 oct
                2026; el que todavía devuelve getlead se ignora. */}
            <LeadRecordFilter options={recordOptions} value={activeRecord} onChange={setRecordFilter} />

            {/* Intereses y consultas: el filtro de arriba recorta esta lista y la actividad */}
            <LeadItemChips
              items={items}
              inquiries={inquiries}
              recordFilter={activeRecord}
              openingItemId={openingItemId}
              onOpen={canOpenItems ? openItem : null}
              savingItemId={stageSavingId}
              onChangeStage={canEditStages ? handleChangeInterestStage : null}
              onAssignRecord={canManageInquiries ? setAssignInquiry : null}
              emptyHint={canManageInquiries
                ? 'Se suman cuando se consulta desde un link compartido o cuando cargás una consulta.'
                : undefined}
              headerAction={canManageInquiries && !isManualInquiryDisabled() ? (
                <Button
                  size="small"
                  onClick={() => setManualOpen(true)}
                  startIcon={<AddCommentOutlinedIcon sx={{ fontSize: '16px !important' }} />}
                  sx={{
                    textTransform: 'none',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: LEADS_TEXT_SECONDARY,
                    py: '2px',
                    px: 0.75,
                    minWidth: 0,
                    '& .MuiButton-startIcon': { mr: 0.5 },
                    '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
                  }}
                >
                  Nueva consulta
                </Button>
              ) : null}
            />

            {/* Nota */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={leadsSectionTitleSx}>Agregar nota</Typography>
              <TextField
                placeholder="Ej.: llamó, quiere visitar el sábado"
                size="small"
                multiline
                minRows={2}
                maxRows={6}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleAddNote();
                  }
                }}
                inputProps={{ maxLength: 4000, 'aria-label': 'Nota' }}
                sx={leadsTextFieldSx}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={!note.trim() || noteSaving}
                  onClick={handleAddNote}
                  sx={leadsPrimaryButtonSx}
                  startIcon={noteSaving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
                >
                  Guardar nota
                </Button>
              </Box>
            </Box>

            <LeadActivity
              entries={activityEntries}
              items={items}
              inquiries={inquiries}
              filterId={activeRecord}
            />
            </>
            )}
          </Box>
        ) : null}
      </Box>

      <LeadManualInquiryDialog
        open={manualOpen}
        ownerUserId={ownerUserId}
        presetLead={presetLead}
        onClose={() => setManualOpen(false)}
        onCreated={handleManualCreated}
      />
      <LeadAssignRecordDialog
        inquiry={assignInquiry}
        ownerUserId={ownerUserId}
        onClose={() => setAssignInquiry(null)}
        onDone={handleAssignDone}
      />
    </>
  );
}
